import { type ComponentProps } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/util/utils';
import { type AsChildProps } from '@/typings/common';

type BadgeProps = ComponentProps<'span'> & VariantProps<typeof badgeVariants> & AsChildProps;

/**
 * ui-badge, READ 11:1107 (lg) and 123:4284 (sm): a rounded-full pill with the label in the
 * condensed uppercase style, tinted by status. The soft variants pair a muted ground with
 * the status colour; outline is a hairline for retired records. Sizes from 2758:204 and
 * 2758:213: sm is 8 across and 3 down on the 10px label, 20 tall; md and lg are 10 across and
 * 5 down on the 12px label, 26 tall.
 */
function Badge({ className, variant, size, asChild = false, ref, ...props }: BadgeProps) {
    const Component = asChild ? Slot : 'span';
    return (
        <Component
            data-slot="badge"
            className={cn(badgeVariants({ variant, size }), className)}
            ref={ref}
            {...props}
        />
    );
}

const badgeVariants = cva(
    'inline-flex shrink-0 items-center justify-center gap-1 rounded-full whitespace-nowrap [&>svg]:size-3',
    {
        variants: {
            variant: {
                secondary: 'bg-secondary text-secondary-foreground',
                primary: 'bg-primary text-primary-foreground',
                success: 'bg-success-muted text-success',
                warning: 'bg-warning-muted text-warning',
                destructive: 'bg-destructive-muted text-destructive',
                outline: 'border-border text-muted-foreground border bg-transparent',
            },
            size: {
                sm: 'text-label-sm px-2 py-[3px] [&>svg]:size-2.5',
                md: 'text-label-md px-2.5 py-[5px]',
                lg: 'text-label-md px-2.5 py-[5px]',
            },
        },
        defaultVariants: { variant: 'secondary', size: 'md' },
    },
);

// eslint-disable-next-line react-refresh/only-export-components
export { Badge, badgeVariants, type BadgeProps };
